// rraStaffClient — AUTHENTICATED staff client for the RESTRICTED RRA console
// (phase 4). Wire shapes mirror apps/api/src/routes/rra.ts. Access is
// double-gated server-side (Keycloak + the RRA_STAFF allowlist); a 403 here
// means the signed-in staff member is not on the console roster.
//
// Distinct from rraFormClient.ts, which is the PUBLIC token-gated client for
// the requester/verifier forms.

import { api, ApiError } from './api';
import { getAccessToken, refreshAccessToken } from '@/lib/authService';
import type { RraFormShape, RraRequestStatement } from './rraFormClient';

export { ApiError };

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

export type RraCaseStatus =
  | 'logged'
  | 'intake_sent'
  | 'intake_complete'
  | 'gate_review'
  | 'verification_pending'
  | 'verification_received'
  | 'evaluation'
  | 'more_info_requested'
  | 'denial_approval'
  | 'dormant'
  | 'decided'
  | 'withdrawn';

export type RraItemStatus =
  | 'open'
  | 'gate_review'
  | 'verification_pending'
  | 'verification_received'
  | 'evaluation'
  | 'more_info_requested'
  | 'denial_approval'
  | 'approved'
  | 'denied'
  | 'withdrawn';

export type RraScope = 'esa_animal' | 'service_animal' | 'other_accommodation' | 'modification';
export type RraChannel = 'phone' | 'email' | 'in_person' | 'mail' | 'other';
/** How a case/item came to exist. Absent = the standard console/intake flow;
 *  'lease_build' = a staff build-time approval made in the lease builder
 *  (WEL-104) — badged in the console, revocable only from the builder path. */
export type RraCaseOrigin = 'lease_build';
export type RraGateSource = 'prior_tenancy' | 'requester_statement' | 'observable' | 'letter';
/** RRA v2.1 (A1): one gate prong's answer. 'not_known_yet' counts as IN the gap. */
export type RraGateAnswer = 'yes' | 'no' | 'not_known_yet';
export type RraWithdrawalChannel = 'email' | 'phone' | 'in_person' | 'letter' | 'other';

export interface RraAnimalInput {
  name?: string;
  type?: 'dog' | 'cat' | 'other';
  otherDescription?: string;
  breed?: string;
  weightLbs?: number;
  age?: number;
  gender?: 'male' | 'female';
  spayedNeutered?: boolean;
  licenseNumber?: string;
}

export interface RraAnimalView extends RraAnimalInput {
  id?: string;
  chargeExemptionCaseItemId?: string;
}

export interface RraGateEvaluation {
  disabilityApparent?: boolean;
  disabilityRationale?: string;
  needApparent?: boolean;
  needRationale?: string;
  trainedServiceAnimal?: boolean;
  source?: RraGateSource;
  outcome?: 'apparent' | 'gap';
  gapScope?: 'disability_and_need' | 'disability_only' | 'need_only';
  evaluatedAt?: string;
  evaluatedByDisplay?: string;
  /** RRA v2.1 (A1): the tri-state answers. */
  disabilityAnswer?: RraGateAnswer;
  needAnswer?: RraGateAnswer;
  /** RRA v2.1 (A1): 1 for the first record, +1 on every Revise. */
  revision?: number;
  /** RRA v2.1 (A1): recorded in the case-open "What do we already know?" step. */
  recordedAtOpen?: boolean;
}

export interface RraDecisionView {
  outcome: 'approved' | 'denied';
  decidedAt: string;
  decidedByDisplay: string;
  denialApprovedBy?: string;
  denialApprovedByDisplay?: string;
  ground?: string;
  /** 'lease_build' when this was an out-of-flow staff build-time approval. */
  origin?: RraCaseOrigin;
}

export interface RraWithdrawalView {
  channel: RraWithdrawalChannel;
  note: string;
  recordedAt: string;
  recordedByDisplay: string;
}

export interface RraInfoRequestView {
  gap: string;
  requestedAt: string;
  requestedByDisplay: string;
}

export interface RraItemView {
  itemId: string;
  scope: RraScope;
  status: RraItemStatus;
  origin?: RraCaseOrigin;
  animal?: RraAnimalView;
  description?: string;
  serviceAnimalQuestions?: { requiredBecauseOfDisability?: boolean; supportProvided?: string };
  /** RRA v2.1: what the requester asked us to change (the waiver is derived from the animal option, so it is not on the wire). */
  request?: RraRequestStatement;
  /** RRA v2.1 (B4): the one optional "how this would help" box (<=300). */
  howChangeHelps?: string;
  attestation?: {
    hasDisability?: boolean;
    needsBecauseOfDisability?: boolean;
    truthful?: boolean;
    attestedAt?: string;
    mode?: 'signed' | 'stated';
    signedName?: string;
    /** RRA v2: the definition the requester attested under (Washington's). */
    definition?: 'rcw_49_60_040';
    /** RRA v2.1: which form generation, and whose voice the lines were in (B2). */
    formVersion?: 'v2.1';
    voice?: 'self' | 'household';
  };
  evaluation?: RraGateEvaluation;
  decision?: RraDecisionView;
  infoRequests?: RraInfoRequestView[];
  withdrawal?: RraWithdrawalView;
  /** Phase 5: the current-tenant Animal Addendum sent on approval (pointer). RRA v2.2: a tenancy
   *  approval's pointer names the household round (`kind: 'household_form'`, `groupId` = the
   *  occupancy); `sentAt` stays empty until the household completes the form and the addendum goes
   *  out. Legacy pointers have no `kind`. */
  addendum?: {
    groupId: string;
    docusealSubmissionId?: number;
    sentAt?: string;
    kind?: 'standalone' | 'household_form' | 'covered_on_file';
    round?: number;
    claimedAt?: string;
  };
  /** RRA v2.2: what a pre-lease approval covers — the animals picked at Approve (names and kinds
   *  only; the exemption pointer lives server-side). Absent on legacy items (they keep `animal`). */
  approvedAnimals?: RraApprovedAnimalView[];
  createdAt: string;
  updatedAt: string;
}

/** RRA v2.2: one animal an approval covers. `id` is a machine ref and is never rendered. */
export interface RraApprovedAnimalView {
  id?: string;
  name?: string;
  type?: 'dog' | 'cat' | 'other';
  breed?: string;
}

/** One AppFolio billing task on the case's freeze ledger (Decision E). RRA v2.2 part B adds the decision
 *  tasks: start charges (a denial on a lease with the pending-decision clause), keep them off (a tenancy
 *  animal request approved), resume them (a tenancy animal request denied). */
export interface RraFreezeActionView {
  actionId: string;
  kind: 'suspend_animal_charges' | 'start_animal_charges' | 'keep_animal_charges_off' | 'resume_animal_charges';
  /** One neutral line of detail the API projects ("Keep animal charges off for Rex"). */
  summary?: string;
  status: 'pending_staff' | 'applied' | 'not_needed';
  createdAt: string;
  notifiedAt?: string;
  appliedAt?: string;
  appliedByDisplay?: string;
}

export interface RraHoldView {
  holdId: string;
  subjectKey: string;
  actionType: 'lease_generation' | 'animal_charge' | 'animal_enforcement' | 'adverse_application_action';
  active: boolean;
  caseId: string;
  reason: string;
  createdAt: string;
  releasedAt?: string;
  releasedByDisplay?: string;
}

export interface RraVerifierView {
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
}

/** RRA v2.1 (E2/A3/G1): one prong's answer. "They couldn't say" is never a negative and records no evidence. */
export type RraProngAnswer = 'yes' | 'no' | 'cant_say';
/** RRA v2.1 (G1): the five call outcomes. */
export type RraFrontlineOutcome = 'not_reached' | 'call_back' | 'wants_writing' | 'spoke' | 'cant_help_yet';
/** RRA v2.1 (G1): why a verifier "can't help yet". */
export type RraFrontlineCantHelpReason =
  | 'wrong_number'
  | 'wants_requester_first'
  | 'needs_own_release'
  | 'cannot_answer_or_declined'
  | 'says_already_answered'
  | 'other';
/** Why front line was asked to phone the verifier. */
export type RraFrontlineReason = 'no_email' | 'unanswered' | 'manual' | 'call_due';
/** RRA v2.1 (G3): the five requester notes. */
export type RraRequesterNoteKind =
  | 'still_open'
  | 'wants_to_hear_from_you'
  | 'own_release_form'
  | 'phone_number'
  | 'verifier_no';
/** How a verifier answered. */
export type RraResponseChannel = 'form' | 'reply_email' | 'letter' | 'oral' | 'uploaded_document';

/** One reported call (RRA v2.1 W3 contract §1). Names, outcomes and dates only. */
export interface RraFrontlineCallView {
  /** Machine ref for the remove-note route ONLY — never rendered. */
  callId: string;
  at: string;
  recordedAt: string;
  callerName: string;
  outcome: RraFrontlineOutcome;
  callBackAt?: string;
  cantHelpReason?: RraFrontlineCantHelpReason;
  betterPhone?: string;
  writing?: { email: string; readBack: boolean; onFile: boolean; sentAt?: string; awaitingStaff?: boolean };
  answers?: { positionToKnow?: 'yes' | 'no'; disability?: RraProngAnswer; need?: RraProngAnswer };
  /** Console only; ABSENT once removed. */
  note?: string;
  /** Set instead of `note` after "Remove note". */
  noteRemoved?: { at: string; byDisplay: string };
}

/** RRA v2 (W3): the front-line phone follow-up on a verification. */
export interface RraFrontlineView {
  triggeredAt: string;
  reason: RraFrontlineReason;
  /** 'Automatic' or the staff member's display name. */
  triggeredByDisplay: string;
  emailedTo: string;
  attempts: number;
  notReachedCount: number;
  lastEmailedAt?: string;
  nextCallSuggestedAt?: string;
  capReachedAt?: string;
  /** "They want to hear from the requester first": no call before this instant. */
  pausedUntil?: string;
  pausedForStaff?: { at: string; reason: 'needs_own_release' | 'wrong_number' | 'other' };
  stopped?: {
    at: string;
    reason: 'cannot_answer_or_declined' | 'says_already_answered' | 'gate_revised';
  };
  /** The latest wants_writing call is waiting for staff to confirm the address. */
  pendingWriting?: { email: string };
  /** Paused for a wrong number and front line gave a better one. */
  pendingPhone?: { phone: string };
  /** STORED order, oldest first (the UI shows newest first). */
  calls: RraFrontlineCallView[];
}

export interface RraVerificationView {
  /** Machine ref (routes) only — never rendered. */
  verificationId: string;
  itemIds: string[];
  verifier: RraVerifierView;
  gapDescription: string;
  status: 'created' | 'sending' | 'sent' | 'reminded' | 'responded' | 'cancelled';
  sentAt?: string;
  openedAt?: string;
  lastReminderAt?: string;
  reminderCount?: number;
  /** Phase 5: reminders exhausted / address bounced — call the verifier. */
  staffCallSuggestedAt?: string;
  bouncedAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  /** RRA v2 (W3): front-line phone follow-up (a verifier with no email, an
   *  unanswered email, or a manual ask). Names, outcomes and dates only. */
  frontline?: RraFrontlineView;
  response?: {
    submittedAt: string;
    channel: RraResponseChannel;
    positionToKnow?: boolean;
    /** NORMALIZED by the server: the tri-state when stored, else mapped from the old booleans. */
    disabilityAnswer?: RraProngAnswer;
    needAnswer?: RraProngAnswer;
    respondent?: { name?: string; email?: string };
    /** A3: the staff member who recorded a reply / letter. */
    recordedByDisplay?: string;
    document: { filename: string; uploadedAt: string } | null;
  };
  /** A4: an answer received after permission was withdrawn — noted, never used. */
  receivedAfterWithdrawal?: { at: string; channel: RraResponseChannel };
  /** G3: requester notes sent because of this verifier. */
  requesterNotes?: Array<{ kind: RraRequesterNoteKind; sentAt: string }>;
  createdAt: string;
  updatedAt: string;
}

/** A3: one document staff added to the case (pointer + display metadata only). */
export interface RraStaffDocumentView {
  /** The handle for the url / permission routes — never rendered. */
  index: number;
  kind: 'letter' | 'reply_email' | 'document' | 'signed_permission';
  filename: string;
  addedAt: string;
  addedByDisplay: string;
  /** Some grant already references this document. */
  permissionRecorded: boolean;
}

export interface RraCaseView {
  caseId: string;
  status: RraCaseStatus;
  testMode: boolean;
  channel: RraChannel;
  origin?: RraCaseOrigin;
  bindings: {
    personKey?: string;
    applicationGroupId?: string;
    occupancyId?: string;
    unitId?: string;
  };
  /** RRA v2 (W2): opened for someone with no application and no unit yet —
   *  nothing is frozen until it is attached. */
  unbound: boolean;
  /** RRA v2 (W2): when an unbound case was attached to an application. */
  attachedAt?: string;
  requester: { name?: string; email?: string; phone?: string; relationship?: string };
  noticeAt: string;
  intake: {
    mode?: 'requester_form' | 'staff_filled';
    sentAt?: string;
    openedAt?: string;
    submittedAt?: string;
    manualSends?: Array<{ at: string; by: string }>;
    lastReminderAt?: string;
    reminderCount?: number;
    /** Phase 5: the requester's address bounced — reminders stopped, case parked. */
    bouncedAt?: string;
  };
  /** Phase 5 (Decision E): the AppFolio billing-task ledger (tenancy cases). */
  freeze: { appfolioActions: RraFreezeActionView[] };
  /** Phase 5: dormant / bounced / stale freeze task / staff-call flag. */
  needsAttention: boolean;
  authorization: {
    granted: boolean;
    withdrawn: boolean;
    signedName?: string;
    signedAt?: string;
    verifiers?: RraVerifierView[];
    hasDocument?: boolean;
  };
  uploadedLetters: Array<{ filename: string; uploadedAt: string }>;
  /** RRA v2.1 (W1): the "Permission to contact" slice — every signing, the open ask, the A6 button. */
  permission: RraPermissionView;
  /** RRA v2.1 (A3): letters, replies and documents staff added. */
  staffDocuments: RraStaffDocumentView[];
  /** The people who may be contacted TODAY: named by a live grant (not withdrawn, not expired).
   *  The ONLY list the console offers for contact — the aggregate `authorization.verifiers` still
   *  names someone whose grant has expired. */
  contactableVerifiers: Array<{ name?: string; role?: string; email?: string; phone?: string }>;
  withdrawal?: RraWithdrawalView;
  items: RraItemView[];
  holds: RraHoldView[];
  freezeActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdByDisplay?: string;
  // --- RRA v2.1 (W2) -----------------------------------------------------
  /** A2: every reason this case needs staff — stored events (clearable) first, then derived ones. */
  attention: RraAttentionView[];
  /** A7: "Waiting on {who} since {date} · {n} business days since we heard." (open cases only). */
  waitingOn: string | null;
  /** A8 (informational, never an attention reason): "Request open for {n} business days". */
  requestOpenLine: string | null;
  /** pre_lease = bound to an application · tenancy = a unit and no application · unbound = neither. */
  kind: 'pre_lease' | 'tenancy' | 'unbound';
  /** B2: who the request is for; null = the requester themselves. */
  subject: { kind: 'household_member'; name: string; minor: boolean } | null;
  /** H2: how an attached case was attached (absent on a case born bound). */
  attachedBy?: 'watcher' | 'staff';
  /** H2: open, bound to an application, and attached after the fact — "Not this application — detach". */
  detachable: boolean;
  /** H1: an unbound, open case with an approved item — "Approved — waiting for {name} to apply". */
  approvedAwaitingApplication: boolean;
  /** A9: the recorded checks, newest last. */
  declineChecks: Array<{ ground: string; recordedAt: string; recordedByDisplay: string }>;
  /** A9: pre-lease and open. */
  canDeclineCheck: boolean;
  /** A4: set while a live grant of permission to contact exists — the signer to name on the button. */
  withdrawablePermission: { signerName: string } | null;
  /** A4: the recorded withdrawal, when there is one. */
  permissionWithdrawal: RraPermissionWithdrawalView | null;
  /** A1: filenames of the letters and documents on file, for the gate's "A letter or document they gave us" source. */
  gateLetterNames: string[];
}

/** A2 / A7: one reason a case needs staff (lib/rra/rraCaseView.ts). */
export interface RraAttentionView {
  /** Echoed to the clear route; never displayed. */
  key: string;
  kind: string;
  text: string;
  raisedAt?: string;
  /** Stored entries only ("Got it"). */
  clearable: boolean;
  /** possible_application only. */
  attach?: { applicationGroupId: string; personKey?: string; label: string };
}

/** A4: the recorded withdrawal of permission to contact. */
export interface RraPermissionWithdrawalView {
  channel: RraWithdrawalChannel;
  receivedAt: string;
  recordedAt: string;
  recordedByDisplay: string;
  notified: Array<{ verifierName: string; sent: boolean; failed?: true; noEmail?: true }>;
}

export interface RraCasesListResponse {
  cases: RraCaseView[];
  /** Reporting counts — testMode cases are EXCLUDED server-side. */
  counts: { open: number; frozen: number; needsAttention: number; total: number; test: number };
}

export interface RraCaseDetailResponse {
  case: RraCaseView;
  verifications: RraVerificationView[];
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export async function listRraCases(): Promise<RraCasesListResponse> {
  return api.get<RraCasesListResponse>('/api/rra/cases');
}

export async function getRraCase(caseId: string): Promise<RraCaseDetailResponse> {
  return api.get<RraCaseDetailResponse>(`/api/rra/cases/${encodeURIComponent(caseId)}`);
}

export interface CreateRraCaseBody {
  bindings: { applicationGroupId?: string; unitId?: string; occupancyId?: string; personKey?: string };
  requester: { name?: string; email?: string; phone?: string; relationship?: 'self' | 'representative' | 'other' };
  requesterApplicantId?: string;
  channel: RraChannel;
  testMode: boolean;
  /** RRA v2 (W2): staff chose "Not an applicant yet" — sent with empty bindings;
   *  the server requires the requester email so the request can attach later. */
  notAnApplicantYet?: boolean;
  /** RRA v2.2: a case carries 1-10 requests, each its own kind and decision. No animal details are
   *  entered here any more — animals are chosen at approval (pre-lease) or listed by the household
   *  (tenancy). `description` is the staff's one-line statement of the request (<=500). */
  items: Array<{ scope: RraScope; description?: string }>;
  /** RRA v2.1 (A1): "What do we already know?" — REQUIRED for every new case. */
  gate: RraGateBody;
  /** RRA v2.1 (B2): who the request is for, when staff already know it is not the requester. */
  subject?: { kind: 'self' } | { kind: 'household_member'; name: string; minor: boolean; email?: string };
}

export async function createRraCase(body: CreateRraCaseBody): Promise<{ case: RraCaseView }> {
  return api.post<{ case: RraCaseView }>('/api/rra/cases', body);
}

export async function sendRraIntake(caseId: string, propertyAddress?: string): Promise<{ sent: true }> {
  return api.post<{ sent: true }>(`/api/rra/cases/${encodeURIComponent(caseId)}/send-intake`, {
    ...(propertyAddress ? { propertyAddress } : {}),
  });
}

/** RRA v2.2: the body of "Add a request" on an open case — one request and its own gate. */
export interface AddRraRequestBody {
  scope: RraScope;
  description?: string;
  /** "What do we already know?" for THIS request (REQUIRED, as at case open). */
  gate: RraGateBody;
  /** An idempotency key for this add: the same key on a retry adds the request once. The panel makes
   *  one per attempt (a retry of the same request reuses it). Field name per W1's route. */
  clientRequestId?: string;
}

/** RRA v2.2: the reply to adding a request. `requesterEmailed` is true when the intake had already
 *  gone out and the "we added to your request" email was sent; a failed email never fails the add. */
export interface AddRraRequestResponse {
  item: RraItemView;
  case: RraCaseView;
  requesterEmailed: boolean;
  /** Why the email did not go (a machine reason such as `no_email`), when it was tried and failed. */
  emailReason?: string;
}

/** RRA v2.2: add a request to an OPEN case (409 once it is closed). */
export async function addRraRequest(caseId: string, body: AddRraRequestBody): Promise<AddRraRequestResponse> {
  return api.post<AddRraRequestResponse>(`/api/rra/cases/${encodeURIComponent(caseId)}/items`, body);
}

/** RRA v2.2: one animal on the household roster (AppFolio animals + tenant-intake animals). `id` is the
 *  animal's stable id, else a name+type key; it rides back on Approve and is never rendered. */
export interface RraRosterAnimal {
  id: string;
  name: string;
  type?: 'dog' | 'cat' | 'other';
  breed?: string;
  source: 'appfolio' | 'intake';
  /** The case item that already holds this animal's $0 exemption (any origin), when one does. */
  exemptByItemId?: string;
  /** The animal is already at $0 for a reason that names no request (a staff build-time note). True
   *  whenever `exemptByItemId` is set too. */
  exempt?: boolean;
  /** RRA v2.2 part B: staff marked this animal `Decision pending` in the lease builder's newest draft
   *  for the household — the Approve picker pre-checks it. */
  pendingInBuilder?: boolean;
}

export interface RraHouseholdAnimalsResponse {
  animals: RraRosterAnimal[];
  /** The roster could not be read (a mirror miss) — the console offers the typed row only. */
  unavailable?: boolean;
}

/** RRA v2.2: the household's animals, for the Approve picker. Empty for tenancy/unbound/test cases. */
export async function getRraHouseholdAnimals(caseId: string): Promise<RraHouseholdAnimalsResponse> {
  return api.get<RraHouseholdAnimalsResponse>(`/api/rra/cases/${encodeURIComponent(caseId)}/household-animals`);
}

/** RRA v2.2: one animal the approval covers — picked from the roster (`id` set) or typed (`id` absent). */
export interface RraApproveAnimal {
  id?: string;
  name: string;
  type?: 'dog' | 'cat' | 'other';
  breed?: string;
}

/** RRA v2.2: the Approve body. `animals` is required on a bound pre-lease animal request (1-10). */
export interface RraApproveBody {
  animals?: RraApproveAnimal[];
}

/** RRA v2.2: replace the animals an approved pre-lease request covers ("Change animals"). */
export async function setRraItemAnimals(
  caseId: string,
  itemId: string,
  body: { animals: RraApproveAnimal[] },
): Promise<{ item: RraItemView; rosterApplied: boolean }> {
  return api.post<{ item: RraItemView; rosterApplied: boolean }>(
    `/api/rra/cases/${encodeURIComponent(caseId)}/items/${encodeURIComponent(itemId)}/animals`,
    body,
  );
}

/** Staff fill-in (the phase-3 endpoint): records the intake AS STATED. RRA v2.1: see StaffFillBody. */
export async function staffFillRraIntake(caseId: string, body: StaffFillBody): Promise<unknown> {
  return api.post(`/api/rra/${encodeURIComponent(caseId)}/staff-intake`, body);
}

/** RRA v2.1 (A1): "What do we already know?" — Yes · No · Not known yet, no default. A rationale is
 *  required for a Yes; a source whenever either answer is Yes. Used by case open AND Revise. */
export interface RraGateBody {
  disabilityAnswer: RraGateAnswer;
  needAnswer: RraGateAnswer;
  disabilityRationale?: string;
  needRationale?: string;
  trainedServiceAnimal?: boolean;
  source?: RraGateSource;
}

/** Revise (or first-record, for a legacy case) the gate on one item. `cancelledAsks` is how many open
 *  requests to verifiers were cancelled because nothing is left to confirm. */
export async function recordRraGate(
  caseId: string,
  itemId: string,
  body: RraGateBody,
): Promise<{ item: RraItemView; cancelledAsks: number }> {
  return api.post<{ item: RraItemView; cancelledAsks: number }>(
    `/api/rra/cases/${encodeURIComponent(caseId)}/items/${encodeURIComponent(itemId)}/gate`,
    body,
  );
}

export async function createRraVerification(
  caseId: string,
  body: { itemIds: string[]; verifier: RraVerifierView & { name: string }; send?: boolean },
): Promise<{
  verification: RraVerificationView;
  send?: { sent: boolean; reason?: string };
  /** RRA v2 (W3): present when the verifier has no email — front line was asked to phone. */
  frontline?: { triggered: true; emailedTo: string } | { triggered: false; reason: string };
}> {
  return api.post(`/api/rra/cases/${encodeURIComponent(caseId)}/verifications`, body);
}

export async function sendRraVerification(
  caseId: string,
  verificationId: string,
): Promise<{ sent: true }> {
  return api.post(
    `/api/rra/cases/${encodeURIComponent(caseId)}/verifications/${encodeURIComponent(verificationId)}/send`,
  );
}

export async function cancelRraVerification(
  caseId: string,
  verificationId: string,
  reason?: string,
): Promise<{ verification: RraVerificationView | null }> {
  return api.post(
    `/api/rra/cases/${encodeURIComponent(caseId)}/verifications/${encodeURIComponent(verificationId)}/cancel`,
    { ...(reason ? { reason } : {}) },
  );
}

export interface RraDecisionResponse {
  item: RraItemView;
  caseStatus: RraCaseStatus;
  holdsReleased: string[];
  rosterApplied?: boolean;
  rosterError?: string;
  /** Phase 5: the Animal Addendum send outcome (tenancy approvals only). RRA v2.2: on a tenancy
   *  approval this is the household animal form going out (`sent: true` = the form was emailed). */
  addendum?: { sent: boolean; reason?: RraAddendumReason; groupId?: string };
  /** RRA v2.1 (H1): approved on a case that has not been attached yet — it applies when they apply. */
  approvedAwaitingApplication?: boolean;
  /** RRA v2.1 (H1): false when the approval email to the requester could not be sent. */
  approvalEmailSent?: boolean;
}

/** Why the Animal Addendum / household animal form did not go on a tenancy approval. The union is
 *  open (`string & {}`) because the API may add reasons; the console words the ones it knows. */
export type RraAddendumReason =
  | 'already_sent'
  | 'prior_send_open'
  | 'not_enabled'
  | 'covered_on_file'
  | 'test_recipient_blocked'
  | 'submission_failed'
  | (string & {});

/** Approve a request. RRA v2.2: a bound pre-lease animal request carries `animals` (the picker). */
export async function approveRraItem(
  caseId: string,
  itemId: string,
  body?: RraApproveBody,
): Promise<RraDecisionResponse> {
  const path = `/api/rra/cases/${encodeURIComponent(caseId)}/items/${encodeURIComponent(itemId)}/approve`;
  return body ? api.post(path, body) : api.post(path);
}

export async function denyRraItem(
  caseId: string,
  itemId: string,
  ground: string,
): Promise<RraDecisionResponse> {
  return api.post(
    `/api/rra/cases/${encodeURIComponent(caseId)}/items/${encodeURIComponent(itemId)}/deny`,
    { ground },
  );
}

export async function requestMoreInfoRraItem(
  caseId: string,
  itemId: string,
  gap: string,
): Promise<{ item: RraItemView }> {
  return api.post(
    `/api/rra/cases/${encodeURIComponent(caseId)}/items/${encodeURIComponent(itemId)}/more-info`,
    { gap },
  );
}

export interface RraWithdrawalEvidenceBody {
  channel: RraWithdrawalChannel;
  note: string;
}

export async function withdrawRraItem(
  caseId: string,
  itemId: string,
  evidence: RraWithdrawalEvidenceBody,
): Promise<RraDecisionResponse> {
  return api.post(
    `/api/rra/cases/${encodeURIComponent(caseId)}/items/${encodeURIComponent(itemId)}/withdraw`,
    { evidence },
  );
}

export async function withdrawRraCase(
  caseId: string,
  evidence: RraWithdrawalEvidenceBody,
): Promise<{ caseStatus: RraCaseStatus; holdsReleased: string[] }> {
  return api.post(`/api/rra/cases/${encodeURIComponent(caseId)}/withdraw`, { evidence });
}

export async function deleteRraTestCase(caseId: string): Promise<{ deleted: true }> {
  return api.post(`/api/rra/cases/${encodeURIComponent(caseId)}/delete-test`);
}

/**
 * WEL-119: mint a short-lived presigned URL to download an applicant-uploaded
 * verification letter (addressed by its index on the case). RRA-staff only +
 * audit-logged server-side; the raw S3 key never reaches the client.
 */
export async function rraLetterDownloadUrl(
  caseId: string,
  index: number,
): Promise<{ url: string; filename: string }> {
  return api.get(
    `/api/rra/cases/${encodeURIComponent(caseId)}/letters/${index}/url`,
  );
}

/**
 * F4: revoke a memorialized BUILD-TIME approval (a "waive fees" that rode a lease
 * send). Any RRA staff may, but a typed reason is required. Withdraws the item +
 * case and clears the per-animal exemption so the animal is no longer $0.
 */
export async function revokeRraBuildApproval(
  caseId: string,
  reason: string,
): Promise<{ revoked: true; caseStatus: RraCaseStatus; advisory?: string }> {
  return api.post(`/api/rra/cases/${encodeURIComponent(caseId)}/revoke-build`, { reason });
}

/** Phase 5 (Decision E): stamp actor + time on a pending AppFolio task. */
export async function markRraFreezeActionApplied(
  caseId: string,
  actionId: string,
): Promise<{ freeze: { appfolioActions: RraFreezeActionView[] } }> {
  return api.post(
    `/api/rra/cases/${encodeURIComponent(caseId)}/freeze-actions/${encodeURIComponent(actionId)}/mark-applied`,
  );
}

/** Phase 5 / RRA v2.1 (E2): the staff ORAL-verification mini-form. Answers only
 *  (Yes / No / They couldn't say) — the body deliberately has no free-text field
 *  (no diagnosis can be recorded). With positionToKnow 'no' the prongs are dropped. */
export interface RecordOralVerificationBody {
  itemIds: string[];
  verifier: { name: string; phone?: string; email?: string; role?: string };
  spokeAt?: string;
  positionToKnow: 'yes' | 'no';
  disability?: RraProngAnswer;
  need?: RraProngAnswer;
}

export async function recordRraOralVerification(
  caseId: string,
  body: RecordOralVerificationBody,
): Promise<{ verification: RraVerificationView }> {
  return api.post(
    `/api/rra/cases/${encodeURIComponent(caseId)}/verifications/record-oral`,
    body,
  );
}

// ---------------------------------------------------------------------------
// WEL-104 (revision, 2026-07-22) — lease-build RA waiver NOTE (the builder's
// per-animal "Approved reasonable accommodation — waive rent & deposit"
// control). The BUILD stage is inert to the RRA system: these endpoints only
// set/clear a transient note on the group's intake roster (zeroing the animal's
// rent + deposit in the preview + generated PDF). The accommodation is
// memorialized into a real RRA case only when the lease is SENT.
// ---------------------------------------------------------------------------

/**
 * Mark one draft-roster animal as an already-approved reasonable accommodation
 * whose rent + deposit are waived, at lease-build time. Writes ONLY the
 * `raWaiveBuildTime` note onto the group's intake roster — NO RRA case is
 * created (that happens at send). `alreadyExempt` is true when the animal is
 * already memorialized (a sent lease / console approval), in which case the
 * builder simply keeps it shown as waived.
 */
export async function designateLeaseBuildRaExemption(body: {
  groupId: string;
  unitId?: string;
  animal: RraAnimalInput & { name: string };
  /** The draft's staff duplicate-resolutions so the server's ambiguity guard
   *  dedups the household's animals the same way the lease does (review #3). */
  petResolutions?: Record<string, 'same' | 'different'>;
}): Promise<{ waived: true; alreadyExempt: boolean }> {
  return api.post('/api/rra/lease-build/waive', body);
}

/**
 * Clear the build-time waiver note (the toggle coming off). Refuses (409) an
 * animal whose accommodation was already memorialized (a sent lease or a
 * console-flow approval) — those are managed in the Accommodations console.
 */
export async function revokeLeaseBuildRaExemption(body: {
  groupId: string;
  animal: RraAnimalInput & { name: string };
}): Promise<{ cleared: boolean }> {
  return api.post('/api/rra/lease-build/waive/clear', body);
}

/** Extract the API's human-readable error message from a thrown ApiError. */
export function rraErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const b = err.body as { error?: unknown; message?: unknown };
    if (typeof b.error === 'string' && b.error) return b.error;
    if (typeof b.message === 'string' && b.message) return b.message;
  }
  return err instanceof Error ? err.message : fallback;
}

// ---------------------------------------------------------------------------
// RRA v2 (W2) — open a request by person; attach an unbound request
// ---------------------------------------------------------------------------

/**
 * One row of the staff person search: an open applicant or a sitting tenant.
 * `name` / `email` / `phone` / `where` are for display (names, roles, addresses
 * — never an id); the binding fields ride back on create and are never shown.
 */
export interface RraPersonHit {
  kind: 'applicant' | 'tenant';
  name: string;
  email?: string;
  phone?: string;
  where: string;
  applicationGroupId?: string;
  personKey?: string;
  unitId?: string;
  occupancyId?: string;
}

/** Search open applications and current tenants by name or email (2+ characters). */
export async function searchRraPeople(q: string): Promise<{ people: RraPersonHit[] }> {
  return api.get<{ people: RraPersonHit[] }>(`/api/rra/people?q=${encodeURIComponent(q)}`);
}

/** Attach an unbound ("not an applicant yet") case to an application by hand. */
export async function attachRraCase(
  caseId: string,
  body: { applicationGroupId: string; personKey?: string },
): Promise<{ case: RraCaseView }> {
  return api.post<{ case: RraCaseView }>(`/api/rra/cases/${encodeURIComponent(caseId)}/attach`, body);
}

/** RRA v2 (W3): ask front line to phone this verifier (any time; repeatable —
 *  re-sends the same email and link). */
export async function triggerRraFrontline(
  caseId: string,
  verificationId: string,
): Promise<{ verification: RraVerificationView }> {
  return api.post(
    `/api/rra/cases/${encodeURIComponent(caseId)}/verifications/${encodeURIComponent(verificationId)}/frontline`,
    {},
  );
}

// --- RRA v2.1 (W1) ---------------------------------------------------------
// Permission to contact (A6, B2, D1), the staff fill-in (I5) and the full request copy (C2).

/** One signing of permission, as the console shows it (names, words and dates — no ids). */
export interface RraPermissionGrantView {
  signedName: string;
  signedAt: string;
  /** "Maria Lopez" for the subject; "Maria Lopez, parent or guardian" for a parent. */
  signerWords: string;
  via: 'request_form' | 'permission_link' | 'uploaded_document';
  verifiers: Array<{ name?: string; role?: string; email?: string; phone?: string }>;
  withdrawn: boolean;
  expired: boolean;
  expiresAt?: string;
}

export type RraSubjectView =
  | { kind: 'self' }
  | { kind: 'household_member'; name: string; minor: boolean; email?: string };

export interface RraPermissionView {
  grants: RraPermissionGrantView[];
  ask: { signerName: string; sentTo: string; sentAt: string; answeredAt?: string; answer?: 'named' | 'letter' } | null;
  /** The intake is in, the case is open, and a gap is recorded. */
  canAsk: boolean;
  /** `Ask ${signerName} who can confirm…` */
  askLabel: string;
  /** An adult household member with no email on file — ask for it first. */
  needsSubjectEmail: boolean;
  subjectName: string;
  subject: RraSubjectView | null;
  /** What the requester form shows — the staff fill-in mirrors it. */
  formShape: RraFormShape;
  /** Submitted on confirm-and-send (B9): nothing attested, nothing signed. */
  intakeConfirmOnly: boolean;
}

/** B2/I5: who the request is for (the same shape the form posts). */
export type StaffSubjectBody = { kind: 'self' } | { kind: 'household_member'; name: string; minor: boolean };

/** I5: the v2.1 staff fill-in — the requester form's items, shape-scoped, every part optional. */
export interface StaffFillBody {
  formVersion: 'v2.1';
  subject?: StaffSubjectBody;
  items: Array<{
    itemId: string;
    request?: { allowAnimal: boolean; otherRule?: string; physicalChange?: string };
    howChangeHelps?: string;
    requiredBecauseOfDisability?: boolean;
    supportProvided?: string;
  }>;
  stated: { hasDisability?: boolean; needsBecauseOfDisability?: boolean };
}

/**
 * A6: send the requester (or the adult household member) the one-screen "who can confirm?" page.
 * An open unanswered ask for the same person is re-sent, never duplicated. `subjectEmail` is for an
 * adult household member whose email is not on file yet.
 */
export async function askRraPermission(
  caseId: string,
  body: { subjectEmail?: string } = {},
): Promise<{ case: RraCaseView }> {
  return api.post<{ case: RraCaseView }>(
    `/api/rra/cases/${encodeURIComponent(caseId)}/permission-ask`,
    body,
  );
}

/** C2: the FULL request copy (what the requester signed, or the stated copy for a staff fill-in). */
export async function rraCaseRequestPdf(caseId: string): Promise<Blob> {
  return api.blob(`/api/rra/cases/${encodeURIComponent(caseId)}/request.pdf`, 'application/pdf');
}



// --- RRA v2.1 (W2) --- attention, permission withdrawal, decline check, detach
// ---------------------------------------------------------------------------

/** A2: "Got it" — clear one stored needs-attention entry (the key comes from `RraAttentionView.key`). */
export async function clearRraAttention(caseId: string, key: string): Promise<{ case: RraCaseView }> {
  return api.post<{ case: RraCaseView }>(
    `/api/rra/cases/${encodeURIComponent(caseId)}/attention/${encodeURIComponent(key)}/clear`,
    {},
  );
}

/** A4: record that the signer withdrew permission to contact. Stops every open ask and tells each
 *  contacted verifier in writing; the request itself stays open. */
export interface RraWithdrawPermissionBody {
  channel: RraWithdrawalChannel;
  /** The date the signer withdrew (YYYY-MM-DD or ISO; not in the future). */
  receivedAt: string;
  /** One neutral line of evidence (5-300 characters). */
  note: string;
}

export async function withdrawRraPermission(
  caseId: string,
  body: RraWithdrawPermissionBody,
): Promise<{ case: RraCaseView; notified: RraPermissionWithdrawalView['notified'] }> {
  return api.post(`/api/rra/cases/${encodeURIComponent(caseId)}/permission/withdraw`, body);
}

/** A9: the recorded check before an application marked "Accommodation pending" is declined. */
export interface RraDeclineCheckBody {
  /** The ground (1-200, neutral). */
  ground: string;
  appliesToEveryApplicant: true;
  unrelatedToRequestOrAnimal: true;
}

export async function recordRraDeclineCheck(
  caseId: string,
  body: RraDeclineCheckBody,
): Promise<{ case: RraCaseView }> {
  return api.post<{ case: RraCaseView }>(`/api/rra/cases/${encodeURIComponent(caseId)}/decline-check`, body);
}

/** H2: "Not this application — detach". Releases the holds the attach wrote and returns the case to "Not yet applied". */
export async function detachRraCase(caseId: string): Promise<{ case: RraCaseView }> {
  return api.post<{ case: RraCaseView }>(`/api/rra/cases/${encodeURIComponent(caseId)}/detach`, {});
}

// --- RRA v2.1 (W3) ---------------------------------------------------------------
// Verification panel + call-sheet follow-through: record a reply or letter (A3),
// add a document (A3/A10), a verifier's own copy (C2), and the console's answers
// to a front-line call (G1: resume / confirm the address / confirm the number /
// remove a note) and the staff-sent "No" note (G3). Wire shapes: the W3 contract.

const RRA_API_BASE = import.meta.env?.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

/**
 * Authenticated multipart POST. `api` only sends JSON (it stamps a JSON
 * Content-Type whenever there is a body), so a file goes through fetch with the
 * same bearer token and one refresh-and-retry on a 401. FormData sets its own
 * boundary header. Throws ApiError (status + parsed body) on a non-2xx, so
 * `rraErrorMessage` reads the server's sentence.
 */
async function postForm<T>(path: string, form: FormData): Promise<T> {
  const send = async (): Promise<Response> => {
    const token = await getAccessToken();
    const headers = new Headers();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(`${RRA_API_BASE}${path}`, { method: 'POST', headers, body: form });
  };
  let res = await send();
  if (res.status === 401 && (await refreshAccessToken())) res = await send();
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* empty or non-JSON body */
    }
    throw new ApiError(res.status, body);
  }
  return (await res.json()) as T;
}

const casePath = (caseId: string): string => `/api/rra/cases/${encodeURIComponent(caseId)}`;
const verificationPath = (caseId: string, verificationId: string): string =>
  `${casePath(caseId)}/verifications/${encodeURIComponent(verificationId)}`;

/** A3: "Record a reply or letter…" — answers only; a file (if any) is uploaded first through the documents route. */
export interface RecordRraReplyBody {
  channel: 'reply_email' | 'letter' | 'uploaded_document';
  /** When staff received it (ISO, not in the future). */
  receivedAt?: string;
  positionToKnow: 'yes' | 'no';
  disability?: RraProngAnswer;
  need?: RraProngAnswer;
  /** Index into the case's staff documents when a file was added. */
  staffDocumentIndex?: number;
}

export async function recordRraReply(
  caseId: string,
  verificationId: string,
  body: RecordRraReplyBody,
): Promise<{ verification: RraVerificationView; usedAsVerification: boolean }> {
  return api.post(`${verificationPath(caseId, verificationId)}/record-reply`, body);
}

export type RraStaffDocumentKind = RraStaffDocumentView['kind'];

/**
 * A3, step one of two: quarantine a PDF on the case (stored, never read). Returns
 * the document's index — the handle the reply / permission steps refer to.
 */
export async function uploadRraStaffDocument(
  caseId: string,
  file: File,
  kind: RraStaffDocumentKind,
  verificationId?: string,
): Promise<{ document: { index: number; kind: RraStaffDocumentKind; filename: string; addedAt: string } }> {
  const form = new FormData();
  // Text fields BEFORE the file part so the server sees them while it streams the PDF.
  form.append('kind', kind);
  if (verificationId) form.append('verificationId', verificationId);
  form.append('file', file);
  return postForm(`${casePath(caseId)}/documents`, form);
}

/** A10: a signed permission staff filed — who signed, when, and the people it names. */
export interface RecordRraPermissionBody {
  signedName: string;
  /** 'YYYY-MM-DD' or an ISO instant. */
  signedAt: string;
  signer: 'subject' | 'parent_guardian';
  verifiers: Array<{ name: string; role?: string; email?: string; phone?: string }>;
}

export async function recordRraPermissionFromDocument(
  caseId: string,
  index: number,
  body: RecordRraPermissionBody,
): Promise<{ case: RraCaseView }> {
  return api.post(`${casePath(caseId)}/documents/${index}/permission`, body);
}

/** Presign (audit-logged server-side) a staff-added document for download. */
export async function rraStaffDocumentUrl(
  caseId: string,
  index: number,
): Promise<{ url: string; filename: string }> {
  return api.get(`${casePath(caseId)}/documents/${index}/url`);
}

/** C2: this verifier's own copy of the signed request (an inline PDF). */
export async function rraVerifierCopyBlob(caseId: string, verificationId: string): Promise<Blob> {
  return api.blob(`${verificationPath(caseId, verificationId)}/copy.pdf`, 'application/pdf');
}

/** G1: clear a pause or stop and set a call due now. */
export async function resumeRraFrontline(
  caseId: string,
  verificationId: string,
): Promise<{ verification: RraVerificationView }> {
  return api.post(`${verificationPath(caseId, verificationId)}/frontline/resume`, {});
}

/** G1 outcome 3: confirm the address they gave, so the request goes to it. */
export async function confirmRraWritingAddress(
  caseId: string,
  verificationId: string,
  email: string,
): Promise<{ verification: RraVerificationView }> {
  return api.post(`${verificationPath(caseId, verificationId)}/frontline/confirm-writing`, { email });
}

/** G1 outcome 5 (wrong number): use the better number front line was given. */
export async function confirmRraPhone(
  caseId: string,
  verificationId: string,
  phone: string,
): Promise<{ verification: RraVerificationView }> {
  return api.post(`${verificationPath(caseId, verificationId)}/frontline/confirm-phone`, { phone });
}

/** G4: remove a call note (stamped with who and when). */
export async function removeRraCallNote(
  caseId: string,
  verificationId: string,
  callId: string,
): Promise<{ verification: RraVerificationView }> {
  return api.post(
    `${verificationPath(caseId, verificationId)}/frontline/calls/${encodeURIComponent(callId)}/remove-note`,
    {},
  );
}

/** G3: the staff-sent "wasn't able to confirm" note to the requester (only after a No). */
export async function sendRraNoNote(
  caseId: string,
  verificationId: string,
): Promise<{ sent: true }> {
  return api.post(`${verificationPath(caseId, verificationId)}/send-no-note`, {});
}
